import type { INonEntityForPost, TEntityData } from '~/types/models'
import type { IOverviewForPost } from '~/types/order/models'
import { hasFileProperty } from '~/cynqueModules/utils/hasFileProperty'

export interface IBuiltPostBody {
  /** The value to hand to fetch as the request body. */
  body: FormData | string
  /** Whether the entity carried a File (i.e. the body is multipart/form-data). */
  hasFileObject: boolean
}

/**
 * Build the POST request body for an entity.
 *
 * - No File present: the entity is JSON-stringified. The caller sets
 *   `content-type: application/json`.
 * - A File present: the body is multipart/form-data — each File is appended as
 *   its own binary part (under its own key), and every other (non-File,
 *   non-undefined) field is bundled into a single JSON `payload` part. This
 *   keeps nested objects (e.g. documentType, the parent reference) and
 *   non-string scalars intact; appending them to FormData directly would coerce
 *   them to strings ("[object Object]", "true", "42"). For the multipart case
 *   the caller must NOT set content-type — the browser adds it with the
 *   multipart boundary.
 *
 * The mock handler (api/msw/modules/parsePostedEntity.ts) and the real server
 * read the file part(s) plus `payload`.
 */
export function buildPostBody<
  T extends TEntityData | IOverviewForPost | INonEntityForPost
>(entity: T): IBuiltPostBody {
  const hasFileObject = hasFileProperty(entity)
  if (!hasFileObject) {
    return { body: JSON.stringify(entity), hasFileObject }
  }

  const formData = new FormData()
  const payload: Record<string, unknown> = {}
  Object.entries(entity).forEach(([key, value]) => {
    if (value instanceof File) {
      formData.append(key, value)
    } else if (value !== undefined) {
      payload[key] = value
    }
  })
  formData.append('payload', JSON.stringify(payload))
  return { body: formData, hasFileObject }
}
